import React from 'react';
import PropTypes from 'prop-types';

import {Chip, Divider, Grid, Stack, Typography, Accordion, AccordionDetails, AccordionSummary} from 'views/components';
import {translate} from 'views/module';

import {buildQuestionMap, formatDateTime, getAnsweredQuestions} from './formUtils';

const FormResponsesCard = (props) => {
  const {form} = props;

  const [expanded, setExpanded] = React.useState(false);

  const questionMap = buildQuestionMap(form?.formDefinitionSnapshot?.questions);
  const answeredQuestions = getAnsweredQuestions(form, questionMap);
  const updatedTime = translate('generic.LastUpdated', {updatedTime: formatDateTime(form?.updatedAt)});
  const {
    caregiverRequirementChips = [],
    carePlanSummaryCaregiver,
    carePlanSummaryClient,
    matchDifficulty,
  } = form?.summary || {};
  const matchDifficultyLabel = matchDifficulty ? `${matchDifficulty[0]}${matchDifficulty.slice(1).toLowerCase()}` : null;
  const carePlanSnapshotLabel = translate('client.ClientDetail.FormSettings.FormsFeed.CarePlanSnapshot');
  const forCaregiversLabel = translate('client.ClientDetail.FormSettings.FormsFeed.ForCaregivers');
  const forClientLabel = translate('client.ClientDetail.FormSettings.FormsFeed.ForClient');
  const matchLabel = matchDifficultyLabel ? translate('client.ClientDetail.FormSettings.FormsFeed.MatchLabel', {difficulty: matchDifficultyLabel}) : null;
  const unansweredLabel = translate('client.ClientDetail.FormSettings.FormsFeed.Unanswered');

  const answeredQuestionEntries = React.useMemo(() => {
    return answeredQuestions.flatMap(({question, displayValue, fieldType}) => {
      if (fieldType === 'schedule') {
        return displayValue.map((event, index) => ({
          question,
          fieldType,
          displayValue: event,
          keySuffix: index,
        }));
      }

      return [{
        question,
        fieldType,
        displayValue,
        keySuffix: 'default',
      }];
    });
  }, [answeredQuestions]);

  const getScheduleDisplay = (scheduleEntry) => {
    if (scheduleEntry.scheduleType === 'flexible') {
      return {
        label: scheduleEntry.label,
        attributes: [
          translate('client.ClientDetail.FormSettings.FormsFeed.Schedule.PreferredDays', {value: scheduleEntry.preferredDays}),
          translate('client.ClientDetail.FormSettings.FormsFeed.Schedule.PreferredTimes', {value: scheduleEntry.preferredTimes}),
          translate('client.ClientDetail.FormSettings.FormsFeed.Schedule.VisitDuration', {value: scheduleEntry.visitDuration}),
        ],
      };
    }

    if (scheduleEntry.scheduleType === 'oncall') {
      return {
        label: scheduleEntry.label,
        attributes: [translate('client.ClientDetail.FormSettings.FormsFeed.Schedule.VisitLength', {value: scheduleEntry.visitLength})],
      };
    }

    return {
      label: scheduleEntry.label,
      attributes: [scheduleEntry.days, scheduleEntry.times, scheduleEntry.dates].filter(Boolean),
    };
  };

  const renderScheduleSlot = (displayValue) => {
    const {label, attributes} = getScheduleDisplay(displayValue);

    return (
      <Stack spacing={0.35}>
        <Typography variant="body1" sx={{fontWeight: 700}}>
          {label}
        </Typography>
        {
          attributes.map((attribute, attributeIndex) => (
            <Typography key={`${label}-${attributeIndex}`} variant="body2" sx={{color: 'text.secondary'}}>
              {attribute}
            </Typography>
          ))
        }
      </Stack>
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
      disableGutters
      sx={{
        'borderRadius': 3,
        'border': '1px solid rgba(120, 144, 180, 0.16)',
        '&:before': {display: 'none'},
      }}
    >
      <AccordionSummary
        sx={{
          px: {xs: 1.5, md: 2},
          py: 1,
        }}
      >
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          justifyContent="space-between"
          alignItems={{xs: 'flex-start', sm: 'center'}}
          spacing={1}
          sx={{width: '100%'}}
        >
          <Stack spacing={0.25}>
            <Typography variant="h6" sx={{fontWeight: 700}}>
              {form?.formDefinitionSnapshot?.title || translate('client.ClientDetail.FormSettings.Title')}
            </Typography>
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
              {updatedTime}
            </Typography>
            {
              carePlanSummaryCaregiver &&
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {carePlanSummaryCaregiver}
              </Typography>
            }
          </Stack>
          {
            form?.formKey &&
            <Chip
              label={form.formKey}
              variant="outlined"
              sx={{fontWeight: 600, borderRadius: 999, backdropFilter: 'blur(8px)'}}
            />
          }
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{px: {xs: 1.5, md: 2}, pb: 2}}>
        <Divider sx={{mb: 1.5}} />

        {
          (carePlanSummaryCaregiver || carePlanSummaryClient || caregiverRequirementChips.length > 0 || matchDifficultyLabel) &&
          <Stack
            spacing={1.25}
            sx={{
              mb: 1.75,
              p: {xs: 1.25, md: 1.75},
              borderRadius: 2.5,
              background: 'linear-gradient(140deg, rgba(80, 116, 255, 0.08), rgba(34, 193, 195, 0.12))',
              border: '1px solid rgba(120, 144, 180, 0.18)',
              boxShadow: '0 14px 36px rgba(15, 23, 42, 0.12)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="overline" sx={{letterSpacing: 0.8, color: 'primary.main', fontWeight: 700}}>
                {carePlanSnapshotLabel}
              </Typography>
              {
                matchDifficultyLabel &&
                <Chip
                  label={matchLabel}
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{borderRadius: 999, fontWeight: 700, px: 0.75}}
                />
              }
            </Stack>

            {
              caregiverRequirementChips.length > 0 &&
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {
                  caregiverRequirementChips.map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      size="small"
                      sx={{
                        borderRadius: 12,
                        background: 'rgba(255,255,255,0.86)',
                        border: '1px solid rgba(120, 144, 180, 0.14)',
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                    />
                  ))
                }
              </Stack>
            }

            {
              carePlanSummaryCaregiver &&
              <Stack spacing={0.25}>
                <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                  {forCaregiversLabel}
                </Typography>
                <Typography variant="body2" sx={{color: 'text.primary', lineHeight: 1.6}}>
                  {carePlanSummaryCaregiver}
                </Typography>
              </Stack>
            }

            {
              carePlanSummaryClient &&
              <Stack spacing={0.25}>
                <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                  {forClientLabel}
                </Typography>
                <Typography variant="body2" sx={{color: 'text.secondary', lineHeight: 1.6}}>
                  {carePlanSummaryClient}
                </Typography>
              </Stack>
            }
          </Stack>
        }

        {
          answeredQuestionEntries.length > 0 ?
          <Grid container spacing={1.5}>
            {
              answeredQuestionEntries.map(({question, displayValue, fieldType, keySuffix}) => (
                <Grid item xs={12} md={6} key={`${question?.questionId || 'schedule'}-${keySuffix}`}>
                  <Stack
                    spacing={0.75}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      background: 'rgba(255,255,255,0.9)',
                      border: '1px solid rgba(120, 144, 180, 0.12)',
                    }}
                  >
                    {
                      fieldType !== 'schedule' &&
                      <Typography variant="subtitle2" sx={{color: 'text.secondary', fontWeight: 600}}>
                        {question?.questionData?.label || question.questionId}
                      </Typography>
                    }
                    {
                      fieldType === 'schedule' && displayValue ?
                      renderScheduleSlot(displayValue) :
                      <Typography variant="body1" sx={{fontWeight: 700}}>
                        {displayValue || unansweredLabel}
                      </Typography>
                    }
                  </Stack>
                </Grid>
              ))
            }
          </Grid> :
          <Typography variant="body2" sx={{color: 'text.secondary'}}>
            {unansweredLabel}
          </Typography>
        }
      </AccordionDetails>
    </Accordion>
  );
};

FormResponsesCard.propTypes = {
  form: PropTypes.object.isRequired,
};

export default React.memo(FormResponsesCard);
